<ng-container *transloco="let t">
  @if (isOpen()) {
    @if (embedded()) {
      <div id="link-loan-mfa-embedded-content">
        @if (isLoading()) {
          <div id="link-loan-mfa-embedded-loading" class="flex flex-col items-center justify-center py-8">
            <mat-spinner diameter="48"></mat-spinner>
          </div>
        } @else {
          @switch (currentStep()) {
            @case ('select-method') {
              @if (errorMessage()) {
                <div
                  id="link-loan-mfa-embedded-error"
                  role="alert"
                  class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                >
                  {{ errorMessage() }}
                </div>
              }
              <p id="link-loan-mfa-embedded-description" class="font-dm text-sm text-ld-neutral-500 mb-6 text-left">
                {{ t('mfaVerificationDescription') }}
              </p>
              @if (getMfaDescription()) {
                <p
                  id="link-loan-mfa-embedded-email-only-desc"
                  class="font-dm text-xs text-ld-neutral-400 mb-4 text-left italic"
                >
                  {{ getMfaDescription() }}
                </p>
              }
              <div class="flex flex-col gap-4 mb-6">
                @if (emailOptions().length > 0) {
                  <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                    <mat-label>{{ t('mfaMethodsEmail') }}</mat-label>
                    <mat-select [value]="initialBsEmail()" (selectionChange)="selectedBsEmail.set($event.value)">
                      @for (option of emailOptions(); track option.value) {
                        <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
                @if (textOptions().length > 0) {
                  <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                    <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
                    <mat-select [value]="initialBsPhone()" (selectionChange)="selectedBsPhone.set($event.value)">
                      @for (option of textOptions(); track option.value) {
                        <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
                @if (callOptions().length > 0) {
                  <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                    <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
                    <mat-select
                      [value]="initialBsCallPhone()"
                      (selectionChange)="selectedBsCallPhone.set($event.value)"
                    >
                      @for (option of callOptions(); track option.value) {
                        <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
              </div>
              @if (!isFirstTimeLinking()) {
                <p id="link-loan-mfa-embedded-faq" class="text-left font-dm text-xs text-ld-neutral-500 mb-6">
                  {{ t('mfaFaqText1') }}
                  <a
                    routerLink="/dashboard"
                    target="_blank"
                    class="text-ld-primary underline cursor-pointer hover:text-ld-primary-hover"
                  >
                    {{ t('mfaFaqLink') }}
                  </a>
                  {{ t('mfaFaqText2') }}
                </p>
              }
              <div class="flex flex-col gap-3">
                @if (emailOptions().length > 0) {
                  <button
                    id="link-loan-mfa-embedded-email-btn"
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onBsSelectEmail()"
                  >
                    <span class="mfa-bs-btn-icon">
                      <img [src]="mailIcon" width="20" height="20" alt="" aria-hidden="true" />
                    </span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaEmail') | uppercase }}</span>
                  </button>
                }
                @if (textOptions().length > 0) {
                  <button
                    id="link-loan-mfa-embedded-text-btn"
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onBsSelectText()"
                  >
                    <span class="mfa-bs-btn-icon">
                      <img [src]="textIcon" width="20" height="20" alt="" aria-hidden="true" />
                    </span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaText') | uppercase }}</span>
                  </button>
                }
                @if (callOptions().length > 0) {
                  <button
                    id="link-loan-mfa-embedded-call-btn"
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onBsSelectCall()"
                  >
                    <span class="mfa-bs-btn-icon">
                      <img [src]="phoneIcon" width="20" height="20" alt="" aria-hidden="true" />
                    </span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaCall') | uppercase }}</span>
                  </button>
                }
              </div>
            }

            @case ('enter-code') {
              <ld-verification-code-card
                #verificationCodeCard
                id="link-loan-mfa-embedded-verification-code"
                [embedded]="true"
                [showLogo]="false"
                [showTitle]="false"
                [maskedContact]="maskedContact()"
                [verificationType]="getVerificationTypeDisplay()"
                [showRememberDevice]="false"
                [showFaqLink]="true"
                (verify)="onVerify($event)"
                (resend)="onResend()"
                (cancel)="onVerificationCancel()"
              />
            }

            @case ('success') {
              <div id="link-loan-mfa-embedded-success" class="p-0">
                <div id="link-loan-mfa-embedded-success-header" class="flex items-center gap-2 mb-3">
                  <img [src]="checkmarkIcon" width="21" height="21" alt="" aria-hidden="true" class="shrink-0" />
                  <h2
                    id="link-loan-mfa-embedded-success-title"
                    class="font-dm text-lg font-semibold text-ld-neutral-700 m-0"
                  >
                    {{ t('modalsLinkLoanMfaVerified') }}
                  </h2>
                </div>
                <p id="link-loan-mfa-embedded-success-message" class="font-dm text-sm text-ld-neutral-500 mb-6">
                  {{ t('modalsLinkLoanMfaSuccessMessage') }}
                </p>
                <div class="flex justify-center">
                  <button
                    id="link-loan-mfa-embedded-continue-btn"
                    type="button"
                    class="btn-primary-ld-purple min-w-[100px]"
                    (click)="onSuccessConfirm()"
                  >
                    {{ t('commonButtonsContinue') }}
                  </button>
                </div>
              </div>
            }
          }
        }
      </div>
    } @else if (isBottomSheet()) {
      <div
        id="link-loan-mfa-bs-backdrop"
        class="fixed inset-0 z-[70] flex items-end bg-black/50 keyboard-aware-container"
        [style.padding-bottom.px]="keyboardHeight()"
      >
        <div
          id="link-loan-mfa-bs-container"
          class="bg-white w-full rounded-t-2xl shadow-xl flex flex-col overflow-hidden link-loan-mfa-bottom-sheet-slide-up"
          [style.max-height]="'calc(100dvh - env(safe-area-inset-top, 0px) - ' + keyboardHeight() + 'px)'"
          style="padding-bottom: env(safe-area-inset-bottom, 0px)"
        >
          <div
            id="link-loan-mfa-bs-header"
            class="flex items-center justify-between px-4 py-3 bg-black rounded-t-2xl shrink-0"
          >
            <div class="flex items-center gap-2">
              @if (currentStep() === 'enter-code') {
                <button
                  id="link-loan-mfa-bs-back-btn"
                  type="button"
                  class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
                  aria-label="Go back"
                  (click)="onVerificationCancel()"
                >
                  <mat-icon>arrow_back</mat-icon>
                </button>
              }
              <span class="font-dm text-base font-semibold text-white">{{ t('mfaVerificationTitle') }}</span>
            </div>
            <button
              id="link-loan-mfa-bs-close-btn"
              type="button"
              class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
              aria-label="Close"
              (click)="onMfaCancel()"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div id="link-loan-mfa-bs-content" class="p-6 overflow-y-auto flex-1">
            @if (isLoading()) {
              <div id="link-loan-mfa-bs-loading" class="flex flex-col items-center justify-center py-8">
                <mat-spinner diameter="48"></mat-spinner>
              </div>
            } @else {
              @switch (currentStep()) {
                @case ('select-method') {
                  @if (errorMessage()) {
                    <div
                      id="link-loan-mfa-bs-error"
                      role="alert"
                      class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                    >
                      {{ errorMessage() }}
                    </div>
                  }
                  <p id="link-loan-mfa-bs-description" class="font-dm text-sm text-ld-neutral-500 mb-6 text-left">
                    {{ t('mfaVerificationDescription') }}
                  </p>
                  @if (getMfaDescription()) {
                    <p
                      id="link-loan-mfa-bs-email-only-desc"
                      class="font-dm text-xs text-ld-neutral-400 mb-4 text-left italic"
                    >
                      {{ getMfaDescription() }}
                    </p>
                  }
                  <div class="flex flex-col gap-4 mb-6">
                    @if (emailOptions().length > 0) {
                      <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                        <mat-label>{{ t('mfaMethodsEmail') }}</mat-label>
                        <mat-select [value]="initialBsEmail()" (selectionChange)="selectedBsEmail.set($event.value)">
                          @for (option of emailOptions(); track option.value) {
                            <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                    @if (textOptions().length > 0) {
                      <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                        <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
                        <mat-select [value]="initialBsPhone()" (selectionChange)="selectedBsPhone.set($event.value)">
                          @for (option of textOptions(); track option.value) {
                            <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                    @if (callOptions().length > 0) {
                      <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                        <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
                        <mat-select
                          [value]="initialBsCallPhone()"
                          (selectionChange)="selectedBsCallPhone.set($event.value)"
                        >
                          @for (option of callOptions(); track option.value) {
                            <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                  </div>
                  @if (!isFirstTimeLinking()) {
                    <p id="link-loan-mfa-bs-faq" class="text-left font-dm text-xs text-ld-neutral-500 mb-6">
                      {{ t('mfaFaqText1') }}
                      <a
                        routerLink="/dashboard"
                        target="_blank"
                        class="text-ld-primary underline cursor-pointer hover:text-ld-primary-hover"
                      >
                        {{ t('mfaFaqLink') }}
                      </a>
                      {{ t('mfaFaqText2') }}
                    </p>
                  }
                  <div class="flex flex-col gap-3">
                    @if (emailOptions().length > 0) {
                      <button
                        id="link-loan-mfa-bs-email-btn"
                        type="button"
                        class="btn-primary-ld-purple mfa-bs-action-btn"
                        (click)="onBsSelectEmail()"
                      >
                        <span class="mfa-bs-btn-icon">
                          <img [src]="mailIcon" width="20" height="20" alt="" aria-hidden="true" />
                        </span>
                        <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaEmail') | uppercase }}</span>
                      </button>
                    }
                    @if (textOptions().length > 0) {
                      <button
                        id="link-loan-mfa-bs-text-btn"
                        type="button"
                        class="btn-primary-ld-purple mfa-bs-action-btn"
                        (click)="onBsSelectText()"
                      >
                        <span class="mfa-bs-btn-icon">
                          <img [src]="textIcon" width="20" height="20" alt="" aria-hidden="true" />
                        </span>
                        <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaText') | uppercase }}</span>
                      </button>
                    }
                    @if (callOptions().length > 0) {
                      <button
                        id="link-loan-mfa-bs-call-btn"
                        type="button"
                        class="btn-primary-ld-purple mfa-bs-action-btn"
                        (click)="onBsSelectCall()"
                      >
                        <span class="mfa-bs-btn-icon">
                          <img [src]="phoneIcon" width="20" height="20" alt="" aria-hidden="true" />
                        </span>
                        <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaCall') | uppercase }}</span>
                      </button>
                    }
                  </div>
                }

                @case ('enter-code') {
                  <ld-verification-code-card
                    #verificationCodeCard
                    id="link-loan-mfa-bs-verification-code"
                    [embedded]="true"
                    [showLogo]="false"
                    [showTitle]="false"
                    [maskedContact]="maskedContact()"
                    [verificationType]="getVerificationTypeDisplay()"
                    [showRememberDevice]="false"
                    [showFaqLink]="true"
                    (verify)="onVerify($event)"
                    (resend)="onResend()"
                    (cancel)="onVerificationCancel()"
                  />
                }

                @case ('success') {
                  <div id="link-loan-mfa-bs-success" class="p-0">
                    <div id="link-loan-mfa-bs-success-header" class="flex items-center gap-2 mb-3">
                      <img [src]="checkmarkIcon" width="21" height="21" alt="" aria-hidden="true" class="shrink-0" />
                      <h2
                        id="link-loan-mfa-bs-success-title"
                        class="font-dm text-lg font-semibold text-ld-neutral-700 m-0"
                      >
                        {{ t('modalsLinkLoanMfaVerified') }}
                      </h2>
                    </div>
                    <p id="link-loan-mfa-bs-success-message" class="font-dm text-sm text-ld-neutral-500 mb-6">
                      {{ t('modalsLinkLoanMfaSuccessMessage') }}
                    </p>
                    <div class="flex justify-center">
                      <button
                        id="link-loan-mfa-bs-continue-btn"
                        type="button"
                        class="btn-primary-ld-purple min-w-[100px]"
                        (click)="onSuccessConfirm()"
                      >
                        {{ t('commonButtonsContinue') }}
                      </button>
                    </div>
                  </div>
                }
              }
            }
          </div>
        </div>
      </div>
    } @else {
      <ld-base-modal
        [title]="t('mfaVerificationTitle')"
        [showCloseButton]="false"
        [isLoading]="isLoading()"
        [closeOnBackdropClick]="false"
        size="fit"
        (closeModal)="onMfaCancel()"
      >
        <div modal-content>
          @switch (currentStep()) {
            @case ('select-method') {
              <div id="link-loan-mfa-select-method" class="p-0">
                @if (errorMessage()) {
                  <div
                    id="link-loan-mfa-send-error"
                    role="alert"
                    class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                  >
                    {{ errorMessage() }}
                  </div>
                }
                <ld-mfa-card
                  #mfaCard
                  id="link-loan-mfa-card"
                  [embedded]="true"
                  [showLogo]="false"
                  [showTitle]="false"
                  [leftAlign]="true"
                  [emailOptions]="emailOptions()"
                  [textPhoneOptions]="textOptions()"
                  [callPhoneOptions]="callOptions()"
                  [emailOnlyDescription]="getMfaDescription()"
                  [showMfaInfoIcon]="isFirstTimeLinking()"
                  [showFaqLink]="!isFirstTimeLinking()"
                  (emailVerification)="onEmailVerification($event)"
                  (textVerification)="onTextVerification($event)"
                  (callVerification)="onCallVerification($event)"
                  (cancel)="onMfaCancel()"
                />
              </div>
            }

            @case ('enter-code') {
              <div id="link-loan-mfa-enter-code" class="p-0">
                <ld-verification-code-card
                  #verificationCodeCard
                  id="link-loan-verification-code"
                  [embedded]="true"
                  [showLogo]="false"
                  [showTitle]="false"
                  [maskedContact]="maskedContact()"
                  [verificationType]="getVerificationTypeDisplay()"
                  [showRememberDevice]="false"
                  [showFaqLink]="true"
                  (verify)="onVerify($event)"
                  (resend)="onResend()"
                  (cancel)="onVerificationCancel()"
                />
              </div>
            }

            @case ('success') {
              <div id="link-loan-mfa-success" class="p-6">
                <div id="link-loan-mfa-success-header" class="flex items-center gap-2 mb-3">
                  <img [src]="checkmarkIcon" width="21" height="21" alt="" aria-hidden="true" class="shrink-0" />
                  <h2 id="link-loan-mfa-success-title" class="font-dm text-lg font-semibold text-ld-neutral-700">
                    {{ t('modalsLinkLoanMfaVerified') }}
                  </h2>
                </div>

                <p id="link-loan-mfa-success-message" class="font-dm text-sm text-ld-neutral-500 mb-6">
                  {{ t('modalsLinkLoanMfaSuccessMessage') }}
                </p>

                <div class="flex justify-end">
                  <button
                    id="link-loan-mfa-continue-btn"
                    type="button"
                    class="btn-primary-ld-purple"
                    (click)="onSuccessConfirm()"
                  >
                    {{ t('commonButtonsContinue') }}
                  </button>
                </div>
              </div>
            }
          }

          @if (isLoading()) {
            <div id="link-loan-mfa-loading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <mat-spinner diameter="48"></mat-spinner>
            </div>
          }
        </div>
      </ld-base-modal>
    }
  }
</ng-container>
